import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "chat-layout" }
const _hoisted_2 = { class: "chat-container" }
const _hoisted_3 = { class: "friends-panel" }
const _hoisted_4 = { class: "friends-header" }
const _hoisted_5 = { class: "friends-search" }
const _hoisted_6 = { class: "friends-list" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "friend-info" }
const _hoisted_9 = { class: "friend-details" }
const _hoisted_10 = { class: "last-message" }
const _hoisted_11 = { class: "message-time" }
const _hoisted_12 = {
  key: 0,
  class: "chat-panel"
}
const _hoisted_13 = { class: "chat-header" }
const _hoisted_14 = { class: "friend-info" }
const _hoisted_15 = { class: "username" }
const _hoisted_16 = {
  key: 0,
  class: "load-more-container"
}
const _hoisted_17 = {
  key: 1,
  class: "loading-tips"
}
const _hoisted_18 = {
  key: 0,
  class: "message-bubble other-bubble"
}
const _hoisted_19 = { class: "sender-info" }
const _hoisted_20 = { class: "sender-name" }
const _hoisted_21 = { class: "message-content-wrapper" }
const _hoisted_22 = { class: "message-content" }
const _hoisted_23 = { class: "message-time" }
const _hoisted_24 = {
  key: 1,
  class: "message-bubble my-bubble"
}
const _hoisted_25 = { class: "sender-info-my" }
const _hoisted_26 = { class: "sender-name-my" }
const _hoisted_27 = { class: "message-content-wrapper" }
const _hoisted_28 = { class: "message-content" }
const _hoisted_29 = { class: "message-time" }
const _hoisted_30 = { class: "chat-input-area" }
const _hoisted_31 = { class: "send-btn-container" }
const _hoisted_32 = {
  key: 1,
  class: "welcome-panel"
}

import { ref, onMounted, onUnmounted, computed, nextTick, reactive, triggerRef, watch } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElBadge } from 'element-plus';
import { 
  sendUserMessage, 
  getUserMessagePage, 
  markMessageAsRead,
  getUnreadMessageCount 
} from '@/user/api/interaction';
import { getUserFriends } from '@/user/api/userApi';
import { formatPostDate } from '@/utils/date';
import GlobalChatWebSocketService, { ChatMessage } from '@/user/services/globalChatService'; // 使用全局服务

interface Friend {
  id: number;
  name: string;
  avatar: string;
  lastMessage?: {
    content: string;
    createTime: string;
  };
  unreadCount?: number;
}

interface Message {
  id: number;
  fromUserId: number;
  fromUserName: string;
  fromUserAvatar: string;
  content: string;
  createTime: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatView',
  setup(__props) {

const store = useStore();
const messagesContainerRef = ref<HTMLDivElement|null>(null);
const searchQuery = ref('');
const wsConnected = ref(false);

// 好友列表
const friendsList = ref<Friend[]>([]);
const selectedFriend = ref<Friend | null>(null);

// 消息状态
const state = reactive({
  inputMessage: '',
  messages: [] as Message[],
  loadingMore: false,
  currentPage: 0,
  hasMore: true
});

// 计算属性
const currentUserInfo = computed(() => {
  const info = store.state.userInfo;
  console.log('当前用户信息:', info);
  return info;
});
const filteredFriends = computed(() => {
  if (!searchQuery.value) {
    return friendsList.value;
  }
  return friendsList.value.filter(friend => 
    friend.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// 选择好友
const selectFriend = async (friend: Friend) => {
  const previousFriendId = selectedFriend.value?.id;
  selectedFriend.value = friend;
  
  // 如果切换了好友，且之前的已选好友存在，可以尝试刷新其未读数（可选优化）
  
  // 重置消息状态
  state.messages = [];
  state.currentPage = 0;
  state.hasMore = true;
  
  // 加载该好友的消息，首次加载时滚动到底部
  await loadMessagesForFriend(friend.id, true);
  
  // 标记该好友的消息为已读
  if (friend.id) {
    try {
      await markMessageAsRead(friend.id);
      // 更新本地未读计数
      friend.unreadCount = 0;
    } catch (error) {
      console.error('标记消息已读失败:', error);
    }
  }
  
  // 滚动到底部
  scrollToBottom();
};

// 监听消息容器滚动事件，实现无感加载
const handleScroll = async (event: Event) => {
  const container = event.target as HTMLElement;
  
  // 由于现在消息是按时间正序排列，最早的在上，最新的在下
  // 当接近顶部时，说明用户想查看更早的消息
  if (container.scrollTop <= 200 && state.hasMore && !state.loadingMore) {
    console.log('滚动到顶部，加载更多消息');
    
    // 记录当前滚动位置
    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    
    await loadMoreMessages();
    
    // 等待 DOM 更新后，调整滚动位置以保持视觉连续性
    await nextTick(() => {
      // 计算需要补偿的高度差，以保持滚动位置不变
      const heightDiff = container.scrollHeight - prevScrollHeight;
      container.scrollTop = prevScrollTop + heightDiff;
    });
  }
};

// 格式化时间
const formatDate = (dateStr: string) => {
  return formatPostDate(dateStr);
};

// 获取头像
const getAvatarByUserId = (userId: number) => {
  if (userId === selectedFriend.value?.id) {
    return selectedFriend.value.avatar;
  }
  if (userId === currentUserInfo.value?.id) {
    return currentUserInfo.value?.avatar;
  }
  return null;
};

// 发送消息
const sendMessage = async () => {
  if (!state.inputMessage.trim()) {
    ElMessage.warning('请输入消息内容');
    return;
  }

  if (!selectedFriend.value) {
    ElMessage.warning('请选择好友');
    return;
  }

  const messageContent = state.inputMessage;
  
  try {
    // 发送消息到服务器
    const response = await sendUserMessage({
      toUserId: selectedFriend.value.id,
      content: messageContent
    });

    if (response.code === 1) {
      // 检查当前用户信息是否存在
      if (!currentUserInfo.value) {
        ElMessage.error('用户未登录，无法发送消息');
        return;
      }

      // 构建新消息对象
      const newMessage: Message = {
        id: response.data?.id || Date.now(),
        fromUserId: currentUserInfo.value.id,
        fromUserName: currentUserInfo.value.name || '我',
        fromUserAvatar: currentUserInfo.value.avatar,
        content: messageContent,
        createTime: new Date().toISOString()
      };
      
      // 添加到本地消息列表
      state.messages.push(newMessage);
      
      // 清空输入框
      state.inputMessage = '';
      
      // 滚动到底部
      scrollToBottom();
      
      ElMessage.success('消息发送成功');
    } else {
      ElMessage.error(response.msg || '消息发送失败');
    }
  } catch (error) {
    console.error('发送消息失败:', error);
    ElMessage.error('发送消息失败，请检查网络连接');
  }
};

// 处理发送快捷键
const handleSendWithKeydown = (event: KeyboardEvent) => {
  if (event.shiftKey) return; // 如果按下Shift键，不触发发送
  event.preventDefault(); // 阻止换行
  sendMessage();
};

// 加载与指定好友的消息
const loadMessagesForFriend = async (userId: number, scrollToBottomOnFirstLoad = true) => {
  if (state.loadingMore) return;
  
  const isFirstLoad = state.currentPage === 0; // 判断是否是首次加载
  
  state.loadingMore = true;
  
  try {
    const params = {
      page: state.currentPage + 1,
      pageSize: 20,
      targetUserId: userId
    };

    const response = await getUserMessagePage(params);

    if (response.code === 1) {
      const newMessages = response.data?.records || [];
      
      // 将新获取的历史消息添加到现有消息前面
      state.messages.unshift(...newMessages);
      
      // 按时间戳对所有消息进行排序，确保时间正序（从早到晚）
      state.messages.sort((a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime());
      
      // 更新页码和是否有更多数据的标志
      state.currentPage++;
      state.hasMore = newMessages.length === params.pageSize;
    } else {
      ElMessage.error(response.msg || '获取消息失败');
    }
  } catch (error) {
    console.error('加载消息失败:', error);
    ElMessage.error('加载消息失败');
  } finally {
    state.loadingMore = false;
    // 等待DOM更新后再滚动
    await nextTick();
    
    // 首次加载时滚动到底部
    // 加载更多历史消息时，由 handleScroll 负责恢复滚动位置，此处不进行滚动操作以避免冲突
    if (isFirstLoad && scrollToBottomOnFirstLoad) {
      scrollToBottom();
    }
  }
};

// 加载更多消息
const loadMoreMessages = async () => {
  if (!selectedFriend.value || state.loadingMore || !state.hasMore) return;
  await loadMessagesForFriend(selectedFriend.value.id, false);
};

// 滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
    }
  });
};

// 滚动到顶部
const scrollToTop = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = 0;
    }
  });
};

// 处理接收到的新消息
const handleNewMessage = (message: ChatMessage) => {
  console.log('收到新消息:', message);
  
  // 查找发送者是否在好友列表中
  const friendIndex = friendsList.value.findIndex(f => f.id === message.fromUserId);
  const friend = friendIndex !== -1 ? friendsList.value[friendIndex] : null;

  // 更新好友列表中的最后消息信息和未读数
  if (friend) {
    friend.lastMessage = {
      content: message.content,
      createTime: message.createTime
    };
    
    // 如果当前没有选中该好友，或者该消息不是当前激活窗口发出的（即后台接收），增加未读数
    // 注意：通常自己发的消息不会通过WS推给自己作为"新消息"提醒，除非是多端同步。
    // 假设 message.fromUserId !== currentUserInfo.value.id
    if (selectedFriend.value?.id !== message.fromUserId) {
      friend.unreadCount = (friend.unreadCount || 0) + 1;
      
      // 可选：播放提示音或显示通知
    } else {
      // 如果当前正与该好友聊天，收到对方消息，标记为已读（API调用可能在selectFriend或此处）
      // 这里主要是确保UI上未读数归零
      friend.unreadCount = 0;
    }
  } else {
    // 如果不在好友列表中（例如陌生人消息，视业务逻辑而定），可能需要重新加载好友列表或忽略
    console.warn('收到非好友列表用户的消息:', message.fromUserId);
  }

  // 如果是当前聊天对象发来的消息，添加到当前消息列表
  if (selectedFriend.value && message.fromUserId === selectedFriend.value.id) {
    // 如果WebSocket消息中的用户名或头像为空，尝试从好友列表中获取
    const newMessage: Message = {
      id: message.id,
      fromUserId: message.fromUserId,
      fromUserName: message.fromUserName || friend?.name || selectedFriend.value.name || '未知用户',
      fromUserAvatar: message.fromUserAvatar || friend?.avatar || selectedFriend.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
      content: message.content,
      createTime: message.createTime
    };
    
    state.messages.push(newMessage);
    scrollToBottom();
    
    // 确保标记为已读（防止漏标）
    markMessageAsRead(selectedFriend.value.id).catch(err => console.error('Auto mark read failed', err));
  }
};

// 监听好友选择变化
watch(selectedFriend, (newVal) => {
  if (newVal) {
    scrollToBottom();
  }
}, { immediate: true });

// 初始化WebSocket连接
const initWebSocket = () => {
  // WebSocket 连接现在在用户登录时已经建立，这里只需要添加处理器
  const token = store.state.token || localStorage.getItem('token');
  if (token) {
    console.log('WebSocket 连接应该已经建立，添加消息处理器');
    
    // 添加消息处理器
    GlobalChatWebSocketService.addMessageHandler(handleNewMessage);
    
    // 添加连接状态监听
    GlobalChatWebSocketService.addConnectionHandler((connected) => {
      wsConnected.value = connected;
      console.log('WebSocket连接状态变更:', connected ? '已连接' : '未连接');
    });
    
    // 初始同步状态
    wsConnected.value = GlobalChatWebSocketService.isConnected();
  } else {
    console.warn('未找到token，无法建立WebSocket连接');
  }
};

// 加载好友列表
const loadFriends = async () => {
  try {
    const params = {
      page: 1,
      pageSize: 100  // 获取所有好友
    };
    
    console.log('正在请求好友列表，参数:', params);
    const response = await getUserFriends(params);
    console.log('获取好友列表响应:', response);
    
    let friends = [];
    
    if (response && typeof response === 'object' && 'code' in response) {
      if (response.code === 1) {
        friends = response.data?.records || [];
        console.log('加载的好友数量:', friends.length);
      } else {
        // 如果后端返回错误信息，则显示具体错误
        ElMessage.error(response.msg || '获取好友列表失败');
        console.error('获取好友列表失败:', response.msg);
        loadMockFriends();
        return;
      }
    } else {
      // 响应可能是直接的数据，没有code包装
      friends = response?.records || response || [];
      console.log('直接加载好友列表，数量:', friends.length);
    }

    // 获取每个好友的未读消息数 (如果API支持批量获取最好，否则可能需要单独请求或依赖WS更新)
    // 这里假设 getUserFriends 返回的数据里可能不包含实时未读数，或者我们需要额外请求
    // 为了简化，先赋值基础信息，然后尝试获取未读数映射（如果有这样的API）
    // 如果没有批量获取未读数的API，可以在循环中调用 getUnreadMessageCount (不推荐，性能差)
    // 或者依赖 WebSocket 推送来更新未读数，初始设为0
    
    friendsList.value = friends.map((f: any) => ({
      ...f,
      unreadCount: 0 // 初始化为0，等待WS更新或单独接口
    }));

    // 可选：如果有一个获取所有未读数的接口，可以在这里调用并合并数据
    // try {
    //   const unreadRes = await getUnreadMessageCount();
    //   if(unreadRes.code === 1) {
    //      // 合并 unreadRes.data 到 friendsList
    //   }
    // } catch(e) {}

  } catch (error: any) {
    console.error('获取好友列表失败:', error);
    if (error.response) {
      console.error('响应错误:', error.response.status, error.response.data);
      ElMessage.error(`获取好友列表失败: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      console.error('请求错误:', error.request);
      ElMessage.error('网络错误，请检查网络连接');
    } else {
      console.error('其他错误:', error.message);
      ElMessage.error('获取好友列表失败: ' + error.message);
    }
    
    // 使用模拟数据作为后备方案
    loadMockFriends();
  }
};

// 模拟好友数据的函数
const loadMockFriends = () => {
  console.warn('使用模拟好友数据');
  friendsList.value = [
    { id: 1, name: '张三', avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png', lastMessage: { content: '你好！', createTime: new Date().toISOString() } },
    { id: 2, name: '李四', avatar: 'https://cube.elemecdn.com/e/5d/7fc9c7b41b5a20bb9a5a4823b91f1f46.png', lastMessage: { content: '今天天气不错', createTime: new Date().toISOString() } },
    { id: 3, name: '王五', avatar: 'https://cube.elemecdn.com/a/54/52712631e37d51e5c44ae69e4a1aae85.png', lastMessage: { content: '钩织作品完成啦', createTime: new Date().toISOString() } }
  ];
};

// 初始化
onMounted(async () => {
  console.log('ChatView组件挂载，开始初始化');
  await loadFriends();
  console.log('好友列表加载完成，添加WebSocket处理器');
  initWebSocket();
});

// 页面卸载时清理
onUnmounted(() => {
  console.log('ChatView组件卸载，移除WebSocket处理器');
  GlobalChatWebSocketService.removeMessageHandler(handleNewMessage);
  // 不断开连接，因为其他组件也可能在使用
});

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[3] || (_cache[3] = _createElementVNode("h3", null, "我的好友", -1)),
          _createElementVNode("div", {
            class: _normalizeClass(["connection-status", { connected: wsConnected.value }])
          }, _toDisplayString(wsConnected.value ? '已连接' : '未连接'), 3)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_el_input, {
            modelValue: searchQuery.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
            placeholder: "搜索好友...",
            "prefix-icon": "Search"
          }, null, 8, ["modelValue"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredFriends.value, (friend) => {
            return (_openBlock(), _createElementBlock("div", {
              key: friend.id,
              class: _normalizeClass(['friend-item', { active: selectedFriend.value?.id === friend.id }]),
              onClick: ($event: any) => (selectFriend(friend))
            }, [
              _createVNode(_unref(ElBadge), {
                "is-dot": !!friend.unreadCount && friend.unreadCount > 0,
                class: "item"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_el_avatar, {
                      size: 40,
                      src: friend.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                      class: "friend-avatar"
                    }, null, 8, ["src"]),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("h4", null, _toDisplayString(friend.name), 1),
                      _createElementVNode("p", _hoisted_10, _toDisplayString(friend.lastMessage?.content || '暂无消息'), 1)
                    ]),
                    _createElementVNode("div", _hoisted_11, _toDisplayString(friend.lastMessage?.createTime ? formatDate(friend.lastMessage.createTime) : ''), 1)
                  ])
                ]),
                _: 2
              }, 1032, ["is-dot"])
            ], 10, _hoisted_7))
          }), 128))
        ])
      ]),
      (selectedFriend.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_component_el_avatar, {
                  size: 40,
                  src: selectedFriend.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                  class: "friend-avatar"
                }, null, 8, ["src"]),
                _createElementVNode("span", _hoisted_15, _toDisplayString(selectedFriend.value.name), 1)
              ]),
              _createVNode(_component_el_button, {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedFriend.value = null)),
                icon: "Back"
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("返回好友列表", -1)
                ]))]),
                _: 1
              })
            ]),
            _createElementVNode("div", {
              class: "chat-messages",
              ref_key: "messagesContainerRef",
              ref: messagesContainerRef,
              onScroll: handleScroll
            }, [
              (state.hasMore)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                    (!state.loadingMore)
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          onClick: loadMoreMessages,
                          loading: state.loadingMore,
                          class: "load-more-btn"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(state.currentPage > 0 ? '加载更多...' : '点击加载历史消息'), 1)
                          ]),
                          _: 1
                        }, 8, ["loading"]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_17, "加载中..."))
                  ]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.messages, (msg, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: msg.id || index,
                  class: _normalizeClass(["message-item", {
              'my-message': currentUserInfo.value && msg.fromUserId === currentUserInfo.value.id,
              'other-message': currentUserInfo.value && msg.fromUserId !== currentUserInfo.value.id
            }])
                }, [
                  (currentUserInfo.value && msg.fromUserId !== currentUserInfo.value.id)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                        _createElementVNode("div", _hoisted_19, [
                          _createVNode(_component_el_avatar, {
                            size: 32,
                            src: msg.fromUserAvatar || selectedFriend.value?.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                            class: "sender-avatar"
                          }, null, 8, ["src"]),
                          _createElementVNode("div", _hoisted_20, _toDisplayString(msg.fromUserName || selectedFriend.value?.name || '未知用户'), 1)
                        ]),
                        _createElementVNode("div", _hoisted_21, [
                          _createElementVNode("div", _hoisted_22, _toDisplayString(msg.content), 1),
                          _createElementVNode("div", _hoisted_23, _toDisplayString(formatDate(msg.createTime)), 1)
                        ])
                      ]))
                    : (currentUserInfo.value && msg.fromUserId === currentUserInfo.value.id)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                          _createElementVNode("div", _hoisted_25, [
                            _createVNode(_component_el_avatar, {
                              size: 32,
                              src: currentUserInfo.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                              class: "sender-avatar-my"
                            }, null, 8, ["src"]),
                            _createElementVNode("div", _hoisted_26, _toDisplayString(currentUserInfo.value.name || '我'), 1)
                          ]),
                          _createElementVNode("div", _hoisted_27, [
                            _createElementVNode("div", _hoisted_28, _toDisplayString(msg.content), 1),
                            _createElementVNode("div", _hoisted_29, _toDisplayString(formatDate(msg.createTime)), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                ], 2))
              }), 128))
            ], 544),
            _createElementVNode("div", _hoisted_30, [
              _createVNode(_component_el_input, {
                modelValue: state.inputMessage,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.inputMessage) = $event)),
                type: "textarea",
                rows: 4,
                placeholder: "请输入消息...",
                maxlength: "500",
                "show-word-limit": "",
                onKeydown: _withKeys(handleSendWithKeydown, ["enter"])
              }, null, 8, ["modelValue"]),
              _createElementVNode("div", _hoisted_31, [
                _createVNode(_component_el_button, {
                  type: "primary",
                  onClick: sendMessage,
                  disabled: !state.inputMessage.trim()
                }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createTextVNode("发送", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"])
              ])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_32, [...(_cache[6] || (_cache[6] = [
            _createElementVNode("div", { class: "welcome-content" }, [
              _createElementVNode("h3", null, "选择好友开始聊天"),
              _createElementVNode("p", null, "从左侧好友列表中选择一位好友开始私信交流")
            ], -1)
          ]))]))
    ])
  ]))
}
}

})